<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import { ChevronLeft, ChevronRight } from "@lucide/vue";
import { Button } from "@/components/ui/button";
import { colorForTemperature } from "rainowls-shared";
import {
  getWeatherIconUrl,
  isPrecipitationWeatherCode,
  weatherColors,
} from "@/utils/weatherCodes";
import type { TemperatureUnit } from "@/stores/settings.store";
import { useUnitConversion } from "@/composables/useUnitConversion";
import type { HourlyDataPoint } from "@/models/forecast";

interface Props {
  hours: HourlyDataPoint[];
  unit: TemperatureUnit;
  dark: boolean;
  currentHourIndex?: number;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: "dayInView", index: number): void;
}>();

const { convertSpeed, convertPrecipitation, labels } = useUnitConversion(
  () => props.unit,
);

// Refs
const canvasRef = ref<HTMLCanvasElement | null>(null);
const scrollRef = ref<HTMLDivElement | null>(null);
const viewportWidth = ref(0);
let resizeObserver: ResizeObserver | undefined;
let drawFrame = 0;

// Scroll state for fade indicators
const showLeftFade = ref(false);
const showRightFade = ref(true);
const scrollPosition = ref(0);
const selectedHourIndex = ref<number | null>(null);

// Drag-to-scroll state
const isDragging = ref(false);
const suppressNextCanvasClick = ref(false);
let dragStartX = 0;
let dragScrollLeft = 0;

// Layout constants
const STEP_WIDTH = 65;
const CHART_HEIGHT = 420;
const TOP_PADDING = 60;
const LABEL_BAND_HEIGHT = 120;
const ICON_SIZE = 40;
const ICON_GAP = 12;
const WIND_ICON_SIZE = 16;
const PRECIP_GROUP_HOURS = 4;

// Computed values
const chartWidth = computed(() => props.hours.length * STEP_WIDTH);
const baselineY = computed(() => CHART_HEIGHT - LABEL_BAND_HEIGHT);
const usableHeight = computed(() => baselineY.value - TOP_PADDING);

// Temperature range
const tempRange = computed(() => {
  if (props.hours.length === 0) return { min: 0, max: 100 };
  const temps = props.hours.map((h) => h.temp);
  return {
    min: Math.min(...temps) - 4,
    max: Math.max(...temps) + 4,
  };
});

// Calculate point positions
const points = computed(() => {
  if (props.hours.length === 0) return [];

  const range = tempRange.value.max - tempRange.value.min;

  return props.hours.map((hour, idx) => {
    const x = (idx + 0.5) * STEP_WIDTH;
    const normalized = (hour.temp - tempRange.value.min) / range;
    const y =
      TOP_PADDING + usableHeight.value - normalized * usableHeight.value;
    return { x, y, hour, idx };
  });
});

// Icon image cache
const iconCache = new Map<string, HTMLImageElement>();

// Track current day in view
const currentDayInView = ref(0);
let programmaticScrollTarget: number | null = null;
let programmaticScrollPosition: number | null = null;
let suppressDayInViewEmit = false;

// Scroll track thumb style
const scrollThumbStyle = computed(() => {
  if (!scrollRef.value) return { left: "0%", width: "20%" };
  const el = scrollRef.value;
  const ratio = el.clientWidth / el.scrollWidth;
  const maxScroll = el.scrollWidth - el.clientWidth;
  const leftPercent =
    maxScroll > 0 ? (scrollPosition.value / maxScroll) * (1 - ratio) * 100 : 0;
  return {
    left: `${leftPercent}%`,
    width: `${ratio * 100}%`,
  };
});

// Draw the chart on canvas
let drawVersion = 0;
let scrollResetTimer: ReturnType<typeof setTimeout> | undefined;

/** Coalesce pending canvas updates into a single animation frame. */
function scheduleDraw() {
  if (drawFrame) return;
  drawFrame = requestAnimationFrame(() => {
    drawFrame = 0;
    void draw();
  });
}

// Preload icon for a weather code
function loadIcon(
  weatherCode: number,
  isDay: boolean,
): Promise<HTMLImageElement> {
  const url = getWeatherIconUrl(weatherCode, isDay);

  if (iconCache.has(url)) {
    return Promise.resolve(iconCache.get(url)!);
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      iconCache.set(url, img);
      resolve(img);
    };
    img.onerror = reject;
    img.src = url;
  });
}

// Preload all icons for current data
async function preloadIcons() {
  const promises = props.hours.map((h) => loadIcon(h.weatherCode, h.isDay));
  await Promise.all(promises);
}

// Handle scroll to update fade indicators and detect day in view
function handleScroll() {
  const el = scrollRef.value;
  if (!el) return;

  const scrollLeft = el.scrollLeft;
  const maxScroll = el.scrollWidth - el.clientWidth;

  showLeftFade.value = scrollLeft > 10;
  showRightFade.value = scrollLeft < maxScroll - 10;
  scrollPosition.value = scrollLeft;
  scheduleDraw();

  // Skip day-in-view updates during programmatic scroll or initial load
  if (suppressDayInViewEmit) {
    return;
  }

  if (
    programmaticScrollTarget !== null &&
    programmaticScrollPosition !== null
  ) {
    if (Math.abs(scrollLeft - programmaticScrollPosition) < 2) {
      programmaticScrollTarget = null;
      programmaticScrollPosition = null;
    }
    return;
  }

  // Calculate which hour is centered in view
  const centerX = scrollLeft + el.clientWidth / 2;
  const centerHourIdx = Math.floor(centerX / STEP_WIDTH);

  // Use the forecastDayIndex from the centered hour if available
  // This properly accounts for timezone-aware calendar days
  const centeredHour =
    props.hours[Math.min(centerHourIdx, props.hours.length - 1)];
  const dayIndex =
    centeredHour?.forecastDayIndex ?? Math.floor(centerHourIdx / 24);
  const clampedIndex = Math.max(0, Math.min(dayIndex, 7));

  // Only emit if day changed
  if (clampedIndex !== currentDayInView.value) {
    currentDayInView.value = clampedIndex;
    emit("dayInView", clampedIndex);
  }
}

// Scroll to a specific day
function scrollToDay(dayIndex: number) {
  if (!scrollRef.value) return;
  programmaticScrollTarget = dayIndex;
  currentDayInView.value = dayIndex;

  // Find the first hour that belongs to this forecast day
  const firstHourIdx = props.hours.findIndex(
    (h) => h.forecastDayIndex === dayIndex,
  );
  const targetIdx = firstHourIdx !== -1 ? firstHourIdx : dayIndex * 24;
  const targetScroll = Math.min(
    targetIdx * STEP_WIDTH,
    scrollRef.value.scrollWidth - scrollRef.value.clientWidth,
  );
  programmaticScrollPosition = targetScroll;

  scrollRef.value.scrollTo({
    left: targetScroll,
    behavior: "smooth",
  });
}

// Scroll by a page width
function scrollPage(direction: "left" | "right") {
  if (!scrollRef.value) return;
  const pageWidth = scrollRef.value.clientWidth * 0.8;
  scrollRef.value.scrollBy({
    left: direction === "left" ? -pageWidth : pageWidth,
    behavior: "smooth",
  });
}

// Drag-to-scroll handlers
function onPointerDown(e: PointerEvent) {
  if (!scrollRef.value || e.pointerType === "touch") return;
  isDragging.value = true;
  dragStartX = e.clientX;
  dragScrollLeft = scrollRef.value.scrollLeft;
  scrollRef.value.setPointerCapture(e.pointerId);
}

/** Move the chart while the pointer is dragging. */
function onPointerMove(e: PointerEvent) {
  if (!isDragging.value || !scrollRef.value) return;
  const dx = e.clientX - dragStartX;
  if (Math.abs(dx) > 5) {
    suppressNextCanvasClick.value = true;
  }
  scrollRef.value.scrollLeft = dragScrollLeft - dx;
}

/** Finish dragging and suppress the resulting click when necessary. */
function onPointerUp(e: PointerEvent) {
  if (!isDragging.value) return;
  isDragging.value = false;
  scrollRef.value?.releasePointerCapture(e.pointerId);
}

/** Select the hour under the pointer unless the click ended a drag. */
function onCanvasClick(e: MouseEvent) {
  if (suppressNextCanvasClick.value) {
    suppressNextCanvasClick.value = false;
    return;
  }

  const el = scrollRef.value;
  if (!el) return;

  const rect = el.getBoundingClientRect();
  const x = e.clientX - rect.left + el.scrollLeft;
  const idx = Math.floor(x / STEP_WIDTH);

  if (idx >= 0 && idx < props.hours.length) {
    selectedHourIndex.value = idx;
    draw();
  }
}

/** Clear the selected hour when the forecast changes. */
function resetSelectedHour() {
  selectedHourIndex.value = null;
  scrollToCurrentHour();
  draw();
}

/** Resolve theme colors used by the canvas renderer. */
function chartInk(alpha: number): string {
  return props.dark
    ? `rgba(245, 247, 250, ${alpha})`
    : `rgba(25, 33, 44, ${alpha})`;
}

/** Render the hourly chart for the current data and viewport. */
async function draw() {
  const version = ++drawVersion;
  const canvas = canvasRef.value;
  if (!canvas || props.hours.length === 0) return;

  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  // Preload icons first
  await preloadIcons();
  if (version !== drawVersion || canvas !== canvasRef.value) return;

  // High DPI support
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const width = viewportWidth.value || scrollRef.value?.clientWidth || 1;
  const height = CHART_HEIGHT;

  const pixelWidth = Math.round(width * dpr);
  const pixelHeight = Math.round(height * dpr);
  if (canvas.width !== pixelWidth) canvas.width = pixelWidth;
  if (canvas.height !== pixelHeight) canvas.height = pixelHeight;
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

  // Clear
  ctx.clearRect(0, 0, width, height);
  ctx.translate(-(scrollRef.value?.scrollLeft ?? 0), 0);

  // Draw layers in order
  drawCurrentHourHighlight(ctx);
  drawNightBands(ctx);
  drawPrecipitationBars(ctx);
  drawGridLines(ctx);
  drawBaseline(ctx, chartWidth.value);
  drawTemperatureLine(ctx);
  drawWeatherIcons(ctx);
  drawTemperatureLabels(ctx);
  drawGroupedPrecipLabels(ctx);
  drawSelectedPrecipLabel(ctx);

  drawWindInfo(ctx);
  drawTimeLabels(ctx);
}

/** Highlight the current hour in the visible chart. */
function drawCurrentHourHighlight(ctx: CanvasRenderingContext2D) {
  const currentIdx = props.currentHourIndex ?? 0;
  if (currentIdx >= 0 && currentIdx < props.hours.length) {
    ctx.fillStyle = "rgba(74, 144, 217, 0.18)";
    ctx.fillRect(currentIdx * STEP_WIDTH, 0, STEP_WIDTH, baselineY.value);
  }

  if (
    selectedHourIndex.value !== null &&
    selectedHourIndex.value !== currentIdx
  ) {
    ctx.fillStyle = "rgba(74, 144, 217, 0.28)";
    ctx.fillRect(
      selectedHourIndex.value * STEP_WIDTH,
      0,
      STEP_WIDTH,
      baselineY.value,
    );
  }
}

/** Shade the nighttime forecast periods. */
function drawNightBands(ctx: CanvasRenderingContext2D) {
  props.hours.forEach((hour, idx) => {
    if (!hour.isDay) {
      ctx.fillStyle = props.dark
        ? "rgba(0, 0, 0, 0.2)"
        : "rgba(25, 33, 44, 0.04)";
      ctx.fillRect(idx * STEP_WIDTH, 0, STEP_WIDTH, baselineY.value);
    }
  });
}

/** Draw the chart’s reference grid. */
function drawGridLines(ctx: CanvasRenderingContext2D) {
  ctx.strokeStyle = chartInk(0.08);
  ctx.lineWidth = 1;

  for (let i = 0; i <= props.hours.length; i++) {
    const x = i * STEP_WIDTH;
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, baselineY.value);
    ctx.stroke();
  }
}

/** Draw precipitation amounts at each hourly position. */
function drawPrecipitationBars(ctx: CanvasRenderingContext2D) {
  const maxRainRate = 0.25;
  const maxSnowRate = 2.0;

  props.hours.forEach((hour, idx) => {
    if (hour.rainRate && hour.rainRate > 0) {
      const fraction = Math.min(hour.rainRate / maxRainRate, 1);
      const barHeight = usableHeight.value * fraction;
      const topY = baselineY.value - barHeight;

      ctx.fillStyle = "rgba(34, 197, 94, 0.3)";
      drawTopRoundedRect(ctx, idx * STEP_WIDTH, topY, STEP_WIDTH, barHeight, 5);
    }

    if (hour.snowRate && hour.snowRate > 0) {
      const fraction = Math.min(hour.snowRate / maxSnowRate, 1);
      const barHeight = usableHeight.value * fraction;
      const topY = baselineY.value - barHeight;

      ctx.fillStyle = "rgba(56, 189, 248, 0.4)";
      drawTopRoundedRect(ctx, idx * STEP_WIDTH, topY, STEP_WIDTH, barHeight, 5);
    }

    if (hour.iceRate && hour.iceRate > 0) {
      const fraction = Math.min(hour.iceRate / maxRainRate, 1);
      const barHeight = usableHeight.value * fraction;
      const topY = baselineY.value - barHeight;

      ctx.fillStyle = "rgba(240, 97, 145, 0.55)";
      drawTopRoundedRect(ctx, idx * STEP_WIDTH, topY, STEP_WIDTH, barHeight, 5);
    }
  });
}

/** Draw a rectangle with rounded upper corners. */
function drawTopRoundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
) {
  if (height <= 0) return;
  const r = Math.min(radius, width / 2, height);
  ctx.beginPath();
  ctx.moveTo(x, y + height);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + r);
  ctx.lineTo(x + width, y + height);
  ctx.closePath();
  ctx.fill();
}

/** Draw the horizontal baseline beneath the forecast. */
function drawBaseline(ctx: CanvasRenderingContext2D, width: number) {
  ctx.strokeStyle = chartInk(0.15);
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(0, baselineY.value);
  ctx.lineTo(width, baselineY.value);
  ctx.stroke();
}

/** Draw the temperature curve through the hourly points. */
function drawTemperatureLine(ctx: CanvasRenderingContext2D) {
  if (points.value.length < 2) return;

  // Create gradient using iOS temperature colors
  const gradient = ctx.createLinearGradient(0, 0, chartWidth.value, 0);

  points.value.forEach((point, idx) => {
    const stop = idx / (points.value.length - 1);
    const color = colorForTemperature(point.hour.temp, props.unit);
    gradient.addColorStop(stop, color);
  });

  // Draw temperature line
  ctx.strokeStyle = gradient;
  ctx.lineWidth = 5;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  drawSmoothCurve(ctx);
  ctx.stroke();
}

/** Connect the supplied points with a smooth canvas path. */
function drawSmoothCurve(ctx: CanvasRenderingContext2D) {
  ctx.beginPath();
  ctx.moveTo(points.value[0].x, points.value[0].y);

  for (let i = 1; i < points.value.length; i++) {
    const prev = points.value[i - 1];
    const current = points.value[i];
    const cpX = (prev.x + current.x) / 2;
    const cpY = (prev.y + current.y) / 2;

    if (i === 1) {
      ctx.quadraticCurveTo(prev.x, prev.y, cpX, cpY);
    } else {
      const prevMidX = (points.value[i - 2].x + prev.x) / 2;
      const prevMidY = (points.value[i - 2].y + prev.y) / 2;
      ctx.bezierCurveTo(prevMidX, prevMidY, prev.x, prev.y, cpX, cpY);
    }

    if (i === points.value.length - 1) {
      ctx.quadraticCurveTo(cpX, cpY, current.x, current.y);
    }
  }
}

/** Draw the loaded weather icons above each hour. */
function drawWeatherIcons(ctx: CanvasRenderingContext2D) {
  points.value.forEach((point) => {
    const url = getWeatherIconUrl(point.hour.weatherCode, point.hour.isDay);
    const img = iconCache.get(url);

    if (img) {
      const iconX = point.x - ICON_SIZE / 2;
      const iconY = point.y - ICON_SIZE - ICON_GAP;

      // Draw icon
      ctx.drawImage(img, iconX, iconY, ICON_SIZE, ICON_SIZE);

      // Draw PoP label above icon if precipitation weather
      if (
        isPrecipitationWeatherCode(point.hour.weatherCode) &&
        point.hour.pop > 0
      ) {
        const popText = `${Math.round(point.hour.pop)}%`;
        ctx.font = "600 13px system-ui, sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";

        // Use different color for high probability
        ctx.lineWidth = 3;
        ctx.strokeStyle = props.dark ? "rgba(0, 0, 0, 0.55)" : "#ffffff";
        ctx.strokeText(popText, point.x, iconY - 4);
        ctx.fillStyle = props.dark
          ? point.hour.pop >= 60
            ? weatherColors.popHigh
            : weatherColors.pop
          : "#246784";
        ctx.fillText(popText, point.x, iconY - 4);
      }
    }
  });
}

/** Draw temperature labels beside the hourly curve. */
function drawTemperatureLabels(ctx: CanvasRenderingContext2D) {
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  points.value.forEach((point, idx) => {
    // Show every other label
    if (idx % 2 !== 0 && idx !== 0) return;

    const color = colorForTemperature(point.hour.temp, props.unit);
    const text = `${Math.round(point.hour.temp)}°`;

    // Pill dimensions
    const pillWidth = 36;
    const pillHeight = 22;
    const pillX = point.x - pillWidth / 2;
    const pillY = point.y + 10;

    // Draw pill background
    ctx.fillStyle = color;
    ctx.globalAlpha = 0.9;
    ctx.beginPath();
    ctx.roundRect(pillX, pillY, pillWidth, pillHeight, pillHeight / 2);
    ctx.fill();
    ctx.globalAlpha = 1;

    // Draw bold text
    ctx.font = "600 13px system-ui, sans-serif";
    ctx.fillStyle = "#ffffff";
    ctx.fillText(text, point.x, pillY + pillHeight / 2 + 1);
  });
}

/** Draw hourly wind speed and direction indicators. */
function drawWindInfo(ctx: CanvasRenderingContext2D) {
  ctx.textAlign = "center";
  const windStartY = baselineY.value + 8;

  props.hours.forEach((hour, idx) => {
    const x = (idx + 0.5) * STEP_WIDTH;
    const windDir = hour.windDirection ?? 0;
    const windSpeed = hour.windSpeed ?? 0;

    // Draw wind direction arrow
    ctx.save();
    ctx.translate(x, windStartY + WIND_ICON_SIZE / 2);
    // Wind direction is where the wind is coming FROM, so we add 180° to show where it's going
    ctx.rotate(((windDir + 180) * Math.PI) / 180);

    // Draw arrow pointing up (which after rotation points in wind direction)
    ctx.beginPath();
    ctx.moveTo(0, -WIND_ICON_SIZE / 2); // Arrow tip
    ctx.lineTo(-4, WIND_ICON_SIZE / 2 - 2); // Bottom left
    ctx.lineTo(0, WIND_ICON_SIZE / 2 - 6); // Center notch
    ctx.lineTo(4, WIND_ICON_SIZE / 2 - 2); // Bottom right
    ctx.closePath();

    ctx.fillStyle = chartInk(0.7);
    ctx.fill();

    ctx.restore();

    // Draw wind speed below the arrow (convert based on unit setting)
    const displaySpeed = convertSpeed(windSpeed) ?? windSpeed;
    ctx.font = "600 11px system-ui, sans-serif";
    ctx.fillStyle = chartInk(0.8);
    ctx.fillText(`${displaySpeed}`, x, windStartY + WIND_ICON_SIZE + 14);

    // Draw units
    ctx.font = "9px system-ui, sans-serif";
    ctx.fillStyle = chartInk(0.5);
    ctx.fillText(labels.value.speed, x, windStartY + WIND_ICON_SIZE + 26);
  });
}

/** Draw local forecast dates and times along the chart. */
function drawTimeLabels(ctx: CanvasRenderingContext2D) {
  ctx.textAlign = "center";
  const labelStartY = baselineY.value + 65;

  props.hours.forEach((hour, idx) => {
    const x = (idx + 0.5) * STEP_WIDTH;
    const isNow = hour.time === "NOW";

    if (isNow) {
      // "NOW" label is bold and bright, single line
      ctx.font = "600 14px system-ui, sans-serif";
      ctx.fillStyle = chartInk(1);
      ctx.fillText("NOW", x, labelStartY + 20);
    } else {
      // Split "DAY TIME" into two lines
      const parts = hour.time.split(" ");
      const day = parts[0]; // e.g., "MON"
      const time = parts[1]; // e.g., "3PM"

      // Day label (top line)
      ctx.font = "600 11px system-ui, sans-serif";
      ctx.fillStyle = chartInk(0.6);
      ctx.fillText(day, x, labelStartY + 12);

      // Time label (bottom line)
      ctx.font = "600 13px system-ui, sans-serif";
      ctx.fillStyle = chartInk(0.9);
      ctx.fillText(time, x, labelStartY + 30);
    }
  });
}

/** Format a precipitation amount for the canvas label. */
function formatPrecipAmount(value: number, exact = false): string {
  if (value <= 0) return "0";
  const decimals = exact ? 2 : 1;
  const converted = convertPrecipitation(value, decimals);
  if (converted === null) return "--";

  if (exact) {
    if (converted < 0.01) return `<0.01 ${labels.value.precipitation}`;
    return `${converted.toFixed(2)} ${labels.value.precipitation}`;
  }

  if (converted < 0.05) return `<0.1 ${labels.value.precipitation}`;
  return `${converted.toFixed(1)} ${labels.value.precipitation}`;
}

/** Return a positive precipitation amount or zero. */
function positivePrecipValue(value: number | undefined): number {
  return value !== undefined && Number.isFinite(value) && value > 0 ? value : 0;
}

/** Resolve the shared precipitation color for rain, snow, or ice. */
function precipColor(type: "rain" | "snow" | "ice"): string {
  switch (type) {
    case "snow":
      return weatherColors.snow;
    case "ice":
      return "#f06191";
    case "rain":
    default:
      return "#2f855a";
  }
}

/** Build the precipitation label for a group of hours. */
function groupedPrecipDisplay(
  startIndex: number,
  endIndex: number,
): { value: number; color: string } | null {
  const window = props.hours.slice(startIndex, endIndex + 1);

  const snowTotal = window.reduce(
    (sum, hour) => sum + positivePrecipValue(hour.snowRate),
    0,
  );
  if (snowTotal > 0) return { value: snowTotal, color: precipColor("snow") };

  const rainTotal = window.reduce(
    (sum, hour) => sum + positivePrecipValue(hour.rainRate),
    0,
  );
  if (rainTotal > 0) return { value: rainTotal, color: precipColor("rain") };

  const iceTotal = window.reduce(
    (sum, hour) => sum + positivePrecipValue(hour.iceRate),
    0,
  );
  if (iceTotal > 0) return { value: iceTotal, color: precipColor("ice") };

  return null;
}

/** Build the precipitation label for the selected hour. */
function selectedPrecipDisplay(): { value: number; color: string } | null {
  if (selectedHourIndex.value === null) return null;
  const hour = props.hours[selectedHourIndex.value];
  if (!hour) return null;

  if (positivePrecipValue(hour.snowRate) > 0) {
    return { value: hour.snowRate!, color: precipColor("snow") };
  }
  if (positivePrecipValue(hour.rainRate) > 0) {
    return { value: hour.rainRate!, color: precipColor("rain") };
  }
  if (positivePrecipValue(hour.iceRate) > 0) {
    return { value: hour.iceRate!, color: precipColor("ice") };
  }
  return null;
}

/** Partition forecast hours into precipitation label groups. */
function precipGroupRanges(
  hourCount: number,
): Array<{ startIndex: number; endIndex: number }> {
  if (hourCount <= 0) return [];

  const ranges: Array<{ startIndex: number; endIndex: number }> = [];
  let startIndex = 0;
  const leadingCount =
    hourCount > PRECIP_GROUP_HOURS ? hourCount % PRECIP_GROUP_HOURS : 0;

  if (leadingCount > 0) {
    ranges.push({ startIndex: 0, endIndex: leadingCount - 1 });
    startIndex = leadingCount;
  }

  while (startIndex < hourCount) {
    ranges.push({
      startIndex,
      endIndex: Math.min(startIndex + PRECIP_GROUP_HOURS - 1, hourCount - 1),
    });
    startIndex += PRECIP_GROUP_HOURS;
  }

  return ranges;
}

/** Draw precipitation totals for each visible group. */
function drawGroupedPrecipLabels(ctx: CanvasRenderingContext2D) {
  for (const { startIndex, endIndex } of precipGroupRanges(
    props.hours.length,
  )) {
    if (
      selectedHourIndex.value !== null &&
      selectedHourIndex.value >= startIndex &&
      selectedHourIndex.value <= endIndex &&
      selectedPrecipDisplay() !== null
    ) {
      continue;
    }

    const display = groupedPrecipDisplay(startIndex, endIndex);
    if (!display) continue;

    const leftEdge = startIndex * STEP_WIDTH;
    const rightEdge = (endIndex + 1) * STEP_WIDTH;
    const centerX = (leftEdge + rightEdge) / 2;

    drawPrecipGroupBracket(
      ctx,
      leftEdge,
      rightEdge,
      baselineY.value,
      display.color,
    );
    drawPrecipChip(
      ctx,
      formatPrecipAmount(display.value),
      centerX,
      baselineY.value,
      display.color,
    );
  }
}

/** Draw the detailed precipitation label for the selected hour. */
function drawSelectedPrecipLabel(ctx: CanvasRenderingContext2D) {
  if (selectedHourIndex.value === null) return;
  const display = selectedPrecipDisplay();
  if (!display) return;

  const centerX = (selectedHourIndex.value + 0.5) * STEP_WIDTH;
  drawPrecipChip(
    ctx,
    formatPrecipAmount(display.value, true),
    centerX,
    baselineY.value,
    display.color,
  );
}

/** Draw a bracket spanning a precipitation group. */
function drawPrecipGroupBracket(
  ctx: CanvasRenderingContext2D,
  leftEdge: number,
  rightEdge: number,
  y: number,
  color: string,
) {
  const inset = 4;

  ctx.save();
  ctx.lineCap = "round";
  ctx.strokeStyle = "rgba(0, 0, 0, 0.48)";
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.moveTo(leftEdge + inset, y);
  ctx.lineTo(rightEdge - inset, y);
  ctx.stroke();

  ctx.strokeStyle = hexToRgba(color, 0.9);
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(leftEdge + inset, y);
  ctx.lineTo(rightEdge - inset, y);
  ctx.stroke();

  ctx.lineCap = "butt";
  ctx.strokeStyle = "rgba(0, 0, 0, 0.52)";
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.moveTo(leftEdge + inset, y - 8);
  ctx.lineTo(leftEdge + inset, y + 8);
  ctx.moveTo(rightEdge - inset, y - 8);
  ctx.lineTo(rightEdge - inset, y + 8);
  ctx.stroke();

  ctx.strokeStyle = hexToRgba(color, 0.95);
  ctx.lineWidth = 2.5;
  ctx.beginPath();
  ctx.moveTo(leftEdge + inset, y - 8);
  ctx.lineTo(leftEdge + inset, y + 8);
  ctx.moveTo(rightEdge - inset, y - 8);
  ctx.lineTo(rightEdge - inset, y + 8);
  ctx.stroke();
  ctx.restore();
}

/** Draw a precipitation label and its background. */
function drawPrecipChip(
  ctx: CanvasRenderingContext2D,
  label: string,
  centerX: number,
  centerY: number,
  color: string,
) {
  ctx.font = "600 10px system-ui, sans-serif";
  const textWidth = ctx.measureText(label).width;
  const labelWidth = textWidth + 10;
  const labelHeight = 17;
  const x = centerX - labelWidth / 2;
  const y = centerY - labelHeight / 2;

  ctx.fillStyle = hexToRgba(color, 0.85);
  ctx.beginPath();
  ctx.roundRect(x, y, labelWidth, labelHeight, labelHeight / 2);
  ctx.fill();

  ctx.strokeStyle = "rgba(255, 255, 255, 0.22)";
  ctx.lineWidth = 1;
  ctx.stroke();

  ctx.fillStyle = "#ffffff";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(label, centerX, centerY + 0.5);
}

/** Convert a hex color to a canvas color with the requested opacity. */
function hexToRgba(hex: string, alpha: number): string {
  const h = hex.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Position the chart at the current forecast hour. */
function scrollToCurrentHour() {
  if (!scrollRef.value) return;
  const idx = props.currentHourIndex ?? 0;

  // Suppress dayInView emit during initial scroll to current hour
  // This prevents the weekly forecast selection from jumping to a different day
  suppressDayInViewEmit = true;
  scrollRef.value.scrollLeft = idx * STEP_WIDTH;

  // Re-enable after a short delay to allow user scrolling to work
  clearTimeout(scrollResetTimer);
  scrollResetTimer = setTimeout(() => {
    suppressDayInViewEmit = false;
  }, 100);
}

// Expose scrollToDay method
defineExpose({ scrollToDay });

watch(
  () => props.dark,
  () => draw(),
);

onMounted(() => {
  viewportWidth.value = scrollRef.value?.clientWidth ?? 1;
  resizeObserver = new ResizeObserver(() => {
    viewportWidth.value = scrollRef.value?.clientWidth ?? 1;
    scheduleDraw();
  });
  if (scrollRef.value) resizeObserver.observe(scrollRef.value);
  draw();
  scrollToCurrentHour();
  handleScroll();
});

onUnmounted(() => {
  drawVersion++;
  cancelAnimationFrame(drawFrame);
  resizeObserver?.disconnect();
  clearTimeout(scrollResetTimer);
});

watch(
  [() => props.hours, () => props.currentHourIndex, () => props.unit],
  () => {
    selectedHourIndex.value = null;
    draw();
    scrollToCurrentHour();
    handleScroll();
  },
);
</script>

<template>
  <div class="relative overflow-hidden rounded-2xl">
    <!-- Scroll container -->
    <div
      ref="scrollRef"
      class="scrollbar-hide overflow-x-auto select-none"
      :class="isDragging ? 'cursor-grabbing' : 'cursor-grab'"
      @scroll="handleScroll"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerUp"
    >
      <div :style="{ width: `${chartWidth}px`, minWidth: `${chartWidth}px` }">
        <canvas
          ref="canvasRef"
          class="sticky left-0 block"
          :style="{ width: `${viewportWidth}px`, height: `${CHART_HEIGHT}px` }"
          @click="onCanvasClick"
        />
      </div>
    </div>

    <Button
      v-show="selectedHourIndex !== null"
      variant="surface"
      size="small"
      radius="pill"
      class="now-button absolute top-3 right-3 z-10 h-7 min-h-0 px-3 py-0 text-[13px] leading-none font-semibold shadow-material"
      @click.stop="resetSelectedHour"
    >
      Now
    </Button>

    <!-- Left fade gradient -->
    <div
      v-show="showLeftFade"
      class="pointer-events-none absolute top-0 bottom-0 left-0 w-16 rounded-l-2xl transition-opacity duration-200"
      :class="'bg-linear-to-r from-app-surface to-transparent'"
    />

    <!-- Right fade gradient -->
    <div
      v-show="showRightFade"
      class="pointer-events-none absolute top-0 right-0 bottom-0 w-16 rounded-r-2xl transition-opacity duration-200"
      :class="'bg-linear-to-l from-app-surface to-transparent'"
    />

    <!-- Left chevron -->
    <Button
      v-show="showLeftFade"
      @click="scrollPage('left')"
      aria-label="Earlier hours"
      variant="surface"
      size="icon-small"
      radius="pill"
      class="scroll-chevron absolute top-1/2 left-1 z-10 size-7 -translate-y-1/2"
    >
      <ChevronLeft class="h-5 w-5" />
    </Button>

    <!-- Right chevron -->
    <Button
      v-show="showRightFade"
      @click="scrollPage('right')"
      aria-label="Later hours"
      variant="surface"
      size="icon-small"
      radius="pill"
      class="scroll-chevron absolute top-1/2 right-1 z-10 size-7 -translate-y-1/2"
    >
      <ChevronRight class="h-5 w-5" />
    </Button>

    <!-- Scroll position track -->
    <div class="relative mx-4 mt-1 h-1 rounded-full bg-app-border">
      <div
        class="absolute inset-y-0 rounded-full bg-text-muted transition-all duration-150"
        :style="scrollThumbStyle"
      />
    </div>
  </div>
</template>
